import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { ModalShell } from '../modalsShared';
import CommonDatePicker from '../../../../Components/Datepicker/datepicker';
import dropdwn from '../../../../Assets/images/svg/dar_dropdown_arrow.svg';
import { FormControl, MenuItem, OutlinedInput, Select } from '@mui/material';
import {
  buildValidStatusMessage,
  useProjectStatusOptions,
} from './statusOptionsShared';

const DEFAULT_FORM_STATE = {
  newStatus: '',
  effectiveDate: '',
  remarks: '',
};

const toInputDate = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
    const [dd, mm, yyyy] = text.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  return text;
};

const EditRequestModal = ({
  isOpen,
  request,
  onClose,
  onSubmit,
  isSubmitDisabled = false,
  isRequestLoading = false,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formError, setFormError] = useState('');
  const {
    statusOptions,
    isLoading: isLoadingStatusOptions,
    error: statusOptionsError,
  } = useProjectStatusOptions({
    isOpen,
    includeValue: formData.newStatus,
  });
  const validStatusMessage = useMemo(
    () => buildValidStatusMessage(statusOptions),
    [statusOptions],
  );

  useEffect(() => {
    if (!request) return;
    setFormData({
      newStatus: request.newStatus || '',
      effectiveDate: toInputDate(request.effectiveDate || ''),
      remarks: request.remarks || request.reason || '',
    });
    setFormError('');
  }, [request]);

  const handleSubmit = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (isSubmitDisabled || isLoadingStatusOptions) return;
    if (statusOptionsError || !statusOptions.length) {
      setFormError(statusOptionsError || 'No project statuses are available.');
      return;
    }
    const allowed = statusOptions.map((opt) => opt.toLowerCase());
    const value = String(formData.newStatus || '').trim();
    const isValid = allowed.includes(value.toLowerCase());
    if (!isValid) {
      setFormError(validStatusMessage);
      return;
    }
    setFormError('');
    await onSubmit({ ...request, ...formData });
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Edit request"
      size="medium"
      modalClassName="request-history-submodal"
    >
      <form className="modal-form request-history-edit-form" onSubmit={handleSubmit}>
        {(request?.project?.name || request?.scrn) ? (
          <div className="request-history-project-meta">
            {request?.project?.name ? (
              <div className="request-history-project-meta__item">
                <span className="request-history-project-meta__label">Project name</span>
                <span className="request-history-project-meta__value">{request.project.name}</span>
              </div>
            ) : null}
            {request?.scrn ? (
              <div className="request-history-project-meta__item request-history-project-meta__item--compact">
                <span className="request-history-project-meta__label">Request ID</span>
                <span className="request-history-project-meta__value">{request.scrn}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="input-field-container">
          <label className="input-label" htmlFor="edit-new-status">New status</label>
          <div className="field-wrapper is-select">
            <FormControl fullWidth size="small">
              <Select
                id="edit-new-status"
                displayEmpty
                value={formData.newStatus}
                onChange={(event) => setFormData((prev) => ({ ...prev, newStatus: event.target.value }))}
                input={<OutlinedInput className="input-field request-history-select" />}
                IconComponent={() => <img src={dropdwn} alt="dropdown" className="input-icon" />}
                disabled={isSubmitDisabled || isLoadingStatusOptions || Boolean(statusOptionsError)}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    className: 'request-history-menu',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className="dropdown-placeholder">
                        {isLoadingStatusOptions ? 'Loading statuses...' : 'Select status'}
                      </span>
                    );
                  }
                  return selected;
                }}
                required
              >
                {!statusOptions.length ? (
                  <MenuItem disabled value="">
                    {statusOptionsError || 'No statuses available'}
                  </MenuItem>
                ) : statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className="input-field-container">
          <label className="input-label" htmlFor="edit-effective-date">Effective date</label>
          <div className="field-wrapper is-date-picker request-history-date-picker">
            <CommonDatePicker
              value={formData.effectiveDate ? dayjs(formData.effectiveDate, 'YYYY-MM-DD') : null}
              onChange={(newValue) => {
                if (!newValue || !dayjs(newValue).isValid()) {
                  setFormData((prev) => ({ ...prev, effectiveDate: '' }));
                  return;
                }
                setFormData((prev) => ({ ...prev, effectiveDate: dayjs(newValue).format('YYYY-MM-DD') }));
              }}
              label=""
              placeholder="Select date"
              isDisabled={isSubmitDisabled}
              error={false}
              helperText=""
              displayFormat="YYYY-MM-DD"
            />
          </div>
        </div>

        <div className="input-field-container">
          <label className="input-label" htmlFor="edit-reason">Remarks</label>
          <div className="field-wrapper">
            <textarea
              id="edit-reason"
              rows="3"
              className="input-field request-history-textarea"
              value={formData.remarks}
              onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
              placeholder="Remarks for status change"
              disabled={isSubmitDisabled}
            />
          </div>
        </div>

        {formError || statusOptionsError ? (
          <div className="form-error">{formError || statusOptionsError}</div>
        ) : null}
        {isRequestLoading ? (
          <div className="request-history-loading-note">Loading latest request details...</div>
        ) : null}

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            Update
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

EditRequestModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  request: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitDisabled: PropTypes.bool,
  isRequestLoading: PropTypes.bool,
};

export default EditRequestModal;
